<template>
  <form @submit.prevent="submit" ref="answerForm">
    <div class="tw-mb-8">
      <div v-if="question.answerKind == 'custom'">
      </div>
      <template v-else-if="question.answerKind == 'widget'" >
        <MathAnswerWidgetLoader 
          v-if="question.answerUi?.widget" 
          :widget="question.answerUi.widget"
          @answer="widgetOnAnswer"
        />
      </template>
      <div v-else-if="question.answerKind == 'buttons'" class="tw-text-center">
        <div v-for="(v, i) in options" :key="i" class="tw-inline-block md:tw-m-2">   
          <MathAnwerFormButton 
            :format="question.answerUi?.optionsFormat" 
            :button-data="v" 
            @click.prevent="onButton(v.value)"
          />
        </div>
      </div>
      <div 
        v-else-if="question.answerKind == 'singleop' 
          || question.answerKind == 'multiop'" 
        class="tw-text-left"
      >
        <div v-for="(v, i) in options" :key="i" class="tw-my-8">
          <label>
            <input 
              v-if="question.answerKind == 'multiop'"
              type="checkbox"
              :value="v.value"
              @change="onMultiselect"
            />
            <input
              v-else
              type="radio"
              v-model="simpleAnswer"
              :value="v.value"
              class="tw-inline-block"
            />
            <RenderMathQuestion
              :question-kind="question.answerUi?.optionsFormat || 'text'"
              :question="v.label || v.value"
              class="tw-inline-block tw-ml-4"
            />
          </label>
        </div>
      </div>
      <div v-else-if="question.answerFields" class="tw-text-center tw-mx-auto">
        <div
          v-for="v in question.answerFields"
          :key="v.name"
          class="sf-form-group tw-inline-block tw-mx-2"
        >
          <label v-if="v.label" :class="'g-color-' + v.color">{{
            v.label
          }}</label>
          <MathJsonAnswerField
            v-if="jsonAnswer[v.name] !== undefined"
            v-model="jsonAnswer[v.name]"
            :answer-data="v"
            class="sf-form-control answer-field"
            :class="'!tw-border-actcolor' + v.color"
          />
        </div>
      </div>
      <div v-else class="sf-form-group tw-text-center">
        <input
          type="text"
          v-model="simpleAnswer"
          class="sf-form-control answer-field"
        />
      </div>
    </div>
    <div
      v-if="question.answerKind != 'buttons'"
      class="sf-form-group tw-text-center"
    >
      <button type="submit" class="sf-btn-primary">Submit</button>
    </div>
  </form>
</template>

<script lang="ts">
import {
  SfMathLevelAnswerOption,
  SfMathTestQuestion,
} from "@/types/mathsTypes";
import { PropType, defineComponent } from "vue";
import MathJsonAnswerField from "./MathJsonAnswerField.vue";
import RenderMathQuestion from "../shared/RenderMathQuestion.vue";
import { sfFieldAutofocus, sfFieldFormatter } from "@/types/generalTypes";
import fieldFormatter from "@/func/format/fieldFormatter";
import fieldAutofocus from "@/func/format/fieldAutofocus";
import MathAnswerWidgetLoader from "./MathAnswerWidgetLoader.vue";
import MathAnwerFormButton from "./MathAnwerFormButton.vue";

export default defineComponent({
  components: { 
    MathJsonAnswerField, 
    RenderMathQuestion, 
    MathAnswerWidgetLoader, 
    MathAnwerFormButton
  },
  emits: ["submit"],
  props: {
    question: { type: Object as PropType<SfMathTestQuestion>, required: true },
    testUiElement: { 
      type: Object as PropType<HTMLElement | null>, 
      required: true 
    }
  },
  data() {
    return {
      simpleAnswer: "",
      jsonAnswer: {} as any,
      customFields: null as sfFieldFormatter[] | null,
      fieldAutofocus: null as sfFieldAutofocus | null,
    };
  },
  computed: {
    options(): SfMathLevelAnswerOption[] {
      if (!this.question.answerUi?.options) {
        return [];
      }
      return this.question.answerUi.options;
    },
  },
  mounted() {
    this.refreshQuestion();
  },
  beforeUnmount() {
    this.clearCustomFields();
  },
  methods: {
    submit() {
      const res = this.question.answerFields 
          || this.question.answerKind == 'multiop'
        ? JSON.stringify(this.jsonAnswer)
        : this.simpleAnswer;
      this.$emit("submit", res);
    },
    refreshQuestion() {
      this.simpleAnswer = "";
      if (this.question.answerFields) {
        const answer = {} as any;
        for (const a of this.question.answerFields) {
          answer[a.name] = "";
        }
        this.jsonAnswer = answer;
      } else if (this.question.answerKind == 'multiop') {
        this.jsonAnswer = [];
      } 
      if(this.question.answerKind == 'custom') {
        this.clearCustomFields();
        this.initCustomFields();
      }
      if(this.fieldAutofocus) {
        this.fieldAutofocus.destroy();
        this.fieldAutofocus = null;
      }
      this.$nextTick(this.focusField);
    },
    onButton(bValue: string) {
      if(this.question.answerFields) {
        for(let i in this.jsonAnswer) {
          this.jsonAnswer[i] = bValue;
        }
      } else {
        this.simpleAnswer = bValue;
      }
      this.submit();
    },
    onMultiselect(e: Event) {
      const tgt = e.target as HTMLInputElement;
      if(!tgt.checked) {
        this.jsonAnswer = this.jsonAnswer.filter((v: any) => v != tgt.value);
      } else if(!this.jsonAnswer.includes(tgt.value)) {
        this.jsonAnswer.push(tgt.value);
      } 
    },
    initCustomFields() {
      if(!this.question.answerFields || !this.testUiElement) {
        return;
      }
      this.customFields = [];
      for(let af of this.question.answerFields) {
        const field = this.testUiElement.querySelector(
          'input[data-answer-name="' + af.name + '"]'
        ) as HTMLInputElement;
        if(field) {
          this.customFields.push(
            fieldFormatter(field, af.fullFormat, this.onCustomField)
          );
        }
      }
    },
    clearCustomFields() {
      if(!this.customFields) {
        return;
      }
      for(let f of this.customFields) {
        f.destroy();
      }
    },
    onCustomField(value: string, varName: string) {
      this.jsonAnswer[varName] = value;
    },
    focusField() {
      if(this.testUiElement) {
        this.fieldAutofocus = fieldAutofocus(this.testUiElement, this.submit);
      }
      if(!this.fieldAutofocus) {
        const fields = (
          this.$refs.answerForm as HTMLElement
        ).getElementsByClassName("answer-field");
        if (fields.length > 0) {
          (fields[0] as HTMLInputElement).focus();
        }
      }
    },
    widgetOnAnswer(data: { name: string, value: string | number }[]) {
      for(let d of data) {
        this.jsonAnswer[d.name] = d.value;
      }
    }
  },
  watch: {
    question() {
      this.refreshQuestion();
    },
  },
});
</script>
